import {
  ViewerApp,
  AssetManagerPlugin,
  timeout,
  SSRPlugin,
  mobileAndTabletCheck,
  GBufferPlugin,
  ProgressivePlugin,
  TonemapPlugin,
  SSAOPlugin,
  FrameFadePlugin,
  BloomPlugin,
  TemporalAAPlugin,
  RandomizedDirectionalLightPlugin,
  createStyles,
  GLTFAnimationPlugin,
  AnimationMixer,
  AnimationClip,
  LoopOnce,
  Color,
  AssetImporter,
  Vector3,
} from "webgi";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

async function setupViewer() {
  const viewer = new ViewerApp({
    canvas: document.getElementById("webgi-canvas") as HTMLCanvasElement,
    useRgbm: true,
    useGBufferDepth: true,
    isAntialiased: false,
  });

  const isMobile = mobileAndTabletCheck();

  viewer.renderer.displayCanvasScaling = Math.min(window.devicePixelRatio, 1);

  const manager = await viewer.addPlugin(AssetManagerPlugin);
  const camera = viewer.scene.activeCamera;

  let position = camera.position;
  const target = camera.target;

  const exploreView = document.querySelector(".cam-view-5") as HTMLElement;
  const canvasView = document.getElementById("webgi-canvas") as HTMLElement;
  const canvasContainer = document.getElementById(
    "webgi-canvas-container"
  ) as HTMLElement;
  const exitContainer = document.querySelector(
    ".exit--container"
  ) as HTMLElement;
  const loaderElement = document.querySelector(".loader") as HTMLElement;

  const nextButton = document.getElementById("nextButton") as HTMLElement;
  const backButton = document.getElementById("backButton") as HTMLElement;

  let currentIndex = 0;

  const annotationPositions = [
    new Vector3(0, 1, 0),
    new Vector3(1, 1, 0),
    new Vector3(
      0.48732301890850067,

      0.25,

      1.25
    ),
    new Vector3(
      0.18732301890850067,

      0.15,

      0.1
    ),
  ];
  let annotations = document.querySelectorAll(
    ".annotation"
  ) as NodeListOf<HTMLElement>;

  let spriteBehindObject = false;
  await viewer.addPlugin(GBufferPlugin);
  await viewer.addPlugin(new ProgressivePlugin(32));
  await viewer.addPlugin(new TonemapPlugin(true, true));
  const ssr = await viewer.addPlugin(SSRPlugin);
  const ssao = await viewer.addPlugin(SSAOPlugin);
  await viewer.addPlugin(FrameFadePlugin);
  const bloom = await viewer.addPlugin(BloomPlugin);
  await viewer.addPlugin(TemporalAAPlugin);
  await viewer.addPlugin(RandomizedDirectionalLightPlugin, false);
  await viewer.addPlugin(GLTFAnimationPlugin);

  ssr!.passes.ssr.passObject.lowQualityFrames = 0;
  bloom.pass!.passObject.bloomIterations = 2;
  ssao.passes.ssao.passObject.material.defines.NUM_SAMPLES = 4;

  const importer = manager.importer as AssetImporter;

  importer.addEventListener("onProgress", (ev) => {
    const progressRatio = ev.loaded / ev.total;
    document
      .querySelector(".progress")
      ?.setAttribute("style", `transform: scaleX(${progressRatio})`);
  });

  importer.addEventListener("onLoad", (ev) => {
    introAnimation();
  });

  viewer.renderer.refreshPipeline();

  let mixer: any;
  let clips: any;
  viewer.scene.addEventListener("addSceneObject", (ev) => {
    const object = ev.object;
    if (!object || object.assetType !== "model" || !object.modelObject) return;

    object.modelObject.traverse((obj: any) => {
      if (clips) return;
      clips = obj.animations;
      if (clips.length < 1) return;
      mixer = new AnimationMixer(obj);
      clips.forEach((clip: any) => mixer.clipAction(clip));
    });
  });

  // WEBGi load model
  await manager.addFromPath("./assets/watch5.glb");
  if (window.innerWidth < 600) {
    camera.setCameraOptions({ fov: 65 });
  } else {
    camera.setCameraOptions({ fov: 25 });
  }

  window.addEventListener("resize", () => {
    if (window.innerWidth < 600) {
      camera.setCameraOptions({ fov: 65 });
    } else {
      camera.setCameraOptions({ fov: 25 });
    }
  });

  const watch = viewer.scene.findObjectsByName("Scene")[0];
  console.log(watch);

  const clip = AnimationClip.findByName(clips, "Animation");
  const drillMaterial =
    manager.materials!.findMaterialsByName("Case Metal2.001")[0];
  const drillMaterial2 =
    manager.materials!.findMaterialsByName("Case Metal.001")[0];

  if (watch) {
    drillMaterial.color = new Color("0xc0c3c3").convertSRGBToLinear();
    drillMaterial2.color = new Color("0xc0c3c3").convertSRGBToLinear();
  }

  const action = mixer.existingAction(clip);

  action.enabled = true;
  action.setLoop(LoopOnce);
  action.clampWhenFinished = true;
  action.reset();
  action.play();
  action.paused = true;

  await timeout(1000); // wait 1 sec

  if (camera.controls) camera.controls.enabled = false;

  // WEBGi mobile adjustments
  if (isMobile) {
    ssr.passes.ssr.passObject.stepCount /= 2;
    bloom.enabled = false;
    camera.setCameraOptions({ fov: 65 });
  }

  window.scrollTo(0, 0);

  await timeout(50);

  let annotationVisible = false;

  const positions = [
    {
      cameraPos: {
        x: 7.2099849896,
        y: 0.099284364,
        z: 7.0496170851,
      },
      targetPos: {
        x: 0.0790911847,
        y: -0.1250940841,
        z: 0.421398108,
      },
    },
    {
      cameraPos: {
        x: -5.743583125318574,
        y: -0.43337932145410174,
        z: 8.221058763712913,
      },
      targetPos: { x: 0.079091, y: -0.125094, z: 0.421398 },
    },
    {
      cameraPos: {
        x: 7.731511963113585,
        y: 0.7646140040238106,
        z: 6.40404431689993,
      },
      targetPos: {
        x: 0.18176906552365,
        y: -0.10672354045416188,
        z: 0.3150840665537192,
      },
    },
    {
      cameraPos: {
        x: 0.13471034956942882,
        y: 1.3926978979388163,
        z: 9.931979167876904,
      },
      targetPos: {
        x: -0.015540671101338385,
        y: -0.12035306694001717,
        z: 0.31317554707634454,
      },
    },
  ];

  function animateCamera(index: number) {
    const { cameraPos, targetPos } = positions[index];

    gsap.to(camera.position, {
      duration: 1,
      ease: "power4.inOut",
      x: cameraPos.x,
      y: cameraPos.y,
      z: cameraPos.z,
      onUpdate,
    });

    gsap.to(target, {
      duration: 1,
      ease: "power4.inOut",
      x: targetPos.x,
      y: targetPos.y,
      z: targetPos.z,
      onUpdate,
    });
  }

  // Handle next button click
  nextButton.addEventListener("click", () => {
    if (annotationVisible && currentIndex < positions.length - 1) {
      currentIndex++;
      animateCamera(currentIndex);
      // gsap.to(annotations[currentIndex], { opacity: 1, duration: 0.5 });
      switchAnnotation(currentIndex);

      if (currentIndex === 3) {
        gsap.fromTo(
          ".visit-container",
          {
            opacity: 0,
            x: "200%",
            pointerEvents: "none",
            duration: 0.25,
            ease: "power4.inOut",
          },
          {
            opacity: 1,
            x: "0%",
            pointerEvents: "auto",
            delay: 0.5,
          }
        );
      }
    }
  });

  // Back button handler
  backButton.addEventListener("click", () => {
    if (currentIndex === 0) {
      handleExitButtonClick();
    }
    if (currentIndex > 0) {
      currentIndex--;
      animateCamera(currentIndex);
      // gsap.to(annotations[currentIndex], { opacity: 1, duration: 0.5 });
      switchAnnotation(currentIndex);

      if (currentIndex < 3) {
        gsap.to(".visit-container", {
          opacity: 0,
          x: "200%",
          pointerEvents: "none",
          duration: 0.5,
        });
      }
    }
  });

  function introAnimation() {
    const introTL = gsap.timeline();
    introTL
      .to(".loader", {
        x: "150%",
        duration: 0.8,
        ease: "power4.inOut",
        delay: 1,
      })
      .fromTo(
        position,
        { x: 3.6, y: -0.04, z: -3.93 },
        {
          x: 6.141540525108059,
          y: -0.40233627549349044,
          z: 8.586488794203259,
          duration: 4,
          onUpdate,
        },
        "-=0.8"
      )
      .fromTo(
        target,
        { x: 3.16, y: -0.13, z: 0.51 },
        {
          x: -1.0706979737332138,
          y: -0.18400655636410645,
          z: 1.6629096641888141,
          duration: 4,
          onUpdate,
        },
        "-=4"
      )

      .fromTo(
        ".hero--scroller",
        { opacity: 0, y: "150%" },
        { opacity: 1, y: "0%", ease: "power4.inOut", duration: 1 },
        "-=1"
      )
      .fromTo(
        ".hero--content",
        { opacity: 0, x: "-50%" },
        {
          opacity: 1,
          x: "0%",
          ease: "power4.inOut",
          duration: 1.8,
          onComplete: setupScrollAnimation,
        },
        "-=1"
      );
  }

  function setupScrollAnimation() {
    document.body.style.overflowY = "scroll";
    document.body.removeChild(loaderElement);

    const tl = gsap.timeline({ default: { ease: "none" } });

    // PERFORMANCE SECTION
    tl.to(watch.rotation, {
      x: -Math.PI / 2,
      scrollTrigger: {
        trigger: ".cam-view-2",
        start: "top bottom",
        end: "top top",
        scrub: true,
        immediateRender: false,
      },
      onUpdate,
    })
      .to(position, {
        x: 6.8286198569,
        y: 2.38,
        z: -0.0995101103,
        scrollTrigger: {
          trigger: ".cam-view-2",
          start: "top bottom",
          end: "top top",
          scrub: true,
          immediateRender: false,
        },
        onUpdate,
      })

      .to(target, {
        x: 0,
        y: 0.45,
        z: 0,
        scrollTrigger: {
          trigger: ".cam-view-2",
          start: "top bottom",
          end: "top top",
          scrub: true,
          immediateRender: false,
        },
        onUpdate,
      })

      .to(watch.rotation, {
        x: 0,
        scrollTrigger: {
          trigger: ".cam-view-3",
          start: "top bottom",
          end: "top top",
          scrub: true,
          immediateRender: false,
        },
        onUpdate,
      })
      .to(action, {
        duration: action.getClip().duration,
        time: action.getClip().duration,
        ease: "ease:Circ.easeOut",
        scrollTrigger: {
          trigger: ".cam-view-3",
          start: "top bottom",
          end: "top top",
          scrub: true,
          // pin: true,
        },
        onUpdate,
      })
      .to(position, {
        x: 8.7738276456,
        y: 0.4546095777,
        z: 13.4095666113,
        scrollTrigger: {
          trigger: ".cam-view-3",
          start: "top bottom",
          end: "top top",
          scrub: true,
          immediateRender: false,
        },
        onUpdate,
      })
      .to(target, {
        x: -2.5575207796,
        y: -0.1515766934,
        z: 2.5247786439,
        scrollTrigger: {
          trigger: ".cam-view-3",
          start: "top bottom",
          end: "top top",
          scrub: true,
          immediateRender: false,
        },
        onUpdate,
      })

      .to(".hero--scroller", {
        opacity: 0,
        y: "150%",
        scrollTrigger: {
          trigger: ".cam-view-2",
          start: "top bottom",
          end: "top center",
          scrub: 1,
          immediateRender: false,
          pin: ".hero--scroller--container",
        },
      })

      .to(".hero--content", {
        opacity: 0,
        xPercent: "-100",
        ease: "power4.out",
        scrollTrigger: {
          trigger: ".cam-view-2",
          start: "top bottom",
          end: "top top",
          scrub: 1,
          immediateRender: false,
          pin: ".hero--content",
        },
      })
      .addLabel("start")

      .fromTo(
        ".performance--content",
        { opacity: 0, x: "110%" },
        {
          opacity: 1,
          x: "0%",
          ease: "power4.out",
          scrollTrigger: {
            trigger: ".cam-view-2",
            start: "top bottom",
            end: "top top",
            scrub: 1,
            immediateRender: false,
            pin: ".performance--container",
          },
        }
      )
      .fromTo(
        ".repair--content",
        { opacity: 0, x: "-110%" },
        {
          opacity: 1,
          x: "0%",
          ease: "power4.out",
          scrollTrigger: {
            trigger: ".cam-view-3",
            start: "top 20%",
            end: "top top",
            scrub: 1,
            immediateRender: false,
          },
        }
      )

      .addLabel("Power")

      .to(position, {
        x: -8.6287861477,
        y: 1.838931316,
        z: 8.9131383423,
        scrollTrigger: {
          trigger: ".cam-view-4",
          start: "top bottom",
          end: "top top",
          scrub: true,
          immediateRender: false,
        },
        onUpdate,
      })
      .to(target, {
        x: 1.0142997171,
        y: -0.1216143553,
        z: 1.302559028,
        scrollTrigger: {
          trigger: ".cam-view-4",
          start: "top bottom",
          end: "top top",
          scrub: true,
          immediateRender: false,
        },
        onUpdate,
      })

      // .fromTo(
      //   ".smart--content",
      //   { opacity: 0, y: "130%" },
      //   {
      //     opacity: 1,
      //     y: "0%",
      //     duration: 0.5,
      //     ease: "power4.out",
      //     scrollTrigger: {
      //       trigger: ".cam-view-4",
      //       start: "top 80%",
      //       end: "top top",
      //       scrub: 1,
      //       immediateRender: false,
      //       // pin: ".smart--container",
      //     },
      //   }
      // )

      .addLabel("smart")

      // EXPLORE SECTION

      .fromTo(
        ".explore--content",
        { opacity: 0, x: "130%" },
        {
          opacity: 1,
          x: "0%",
          duration: 0.5,
          ease: "power4.out",
          scrollTrigger: {
            trigger: ".cam-view-5",
            start: "top bottom",
            end: "top top",
            scrub: 1,
            immediateRender: false,
          },
        }
      )
      .addLabel("Explore");
  }

  let needsUpdate = true;
  function onUpdate() {
    needsUpdate = true;
  }

  function updateAnnotationOpacity(index: number) {
    const meshDistance = camera.position.distanceTo(watch.position);
    const spriteDistance = camera.position.distanceTo(
      annotationPositions[index]
    );
    const spriteBehindObject = spriteDistance > meshDistance;

    return spriteBehindObject ? 0.25 : 1;
  }

  function updateScreenPosition(index: number) {
    const vector = annotationPositions[index].clone();
    const canvas = viewer.canvas;
    vector.project(camera.cameraObject);

    vector.x = Math.round(
      (0.5 + vector.x / 2) *
        (canvas.width / viewer.renderer.displayCanvasScaling)
    );
    vector.y = Math.round(
      (0.5 - vector.y / 2) *
        (canvas.height / viewer.renderer.displayCanvasScaling)
    );

    const annotation = annotations[index];
    annotation.style.top = `${vector.y}px`;
    annotation.style.left = `${vector.x}px`;
    // annotation.style.opacity = updateAnnotationOpacity(index).toString();
    annotation.style.opacity = annotationVisible ? "1" : "0";
  }

  function toggleAnnotations(visible: boolean) {
    annotationVisible = visible;
    annotations.forEach((annotation) => {
      gsap.to(annotation, { opacity: visible ? 1 : 0, duration: 0.25 });
    });
  }

  function switchAnnotation(index: number) {
    annotations.forEach((annotation, i) => {
      if (i === index) {
        // Animate showing the current annotation
        gsap.to(annotation, { opacity: 1, duration: 0.25 });
      } else {
        // Animate hiding the other annotations
        gsap.to(annotation, { opacity: 0, duration: 0.25 });
      }
    });
  }

  // updateAnnotationOpacity();
  updateScreenPosition(currentIndex);

  viewer.addEventListener("postRender", () => {
    // updateAnnotationOpacity(currentIndex);
    updateScreenPosition(currentIndex);
  });

  if (!isMobile) {
  } else {
    createStyles(`
  html, body {
    scroll-snap-type: y mandatory;
  }

          `);
  }

  viewer.addEventListener("preFrame", () => {
    if (mixer) mixer.update(0.016);
    if (needsUpdate) {
      camera.positionUpdated(false);
      camera.targetUpdated(true);
      needsUpdate = false;
    }
  });

  // EXPLORE ALL FEATURES EVENT
  document.querySelector(".button-explore")?.addEventListener("click", () => {
    annotationVisible = true;
    exploreView.style.pointerEvents = "none";
    canvasView.style.pointerEvents = "all";
    if (camera.controls) {
      camera.controls.enableZoom = false;
    }
    canvasContainer.style.zIndex = "1";
    document.body.style.overflowY = "hidden";
    document.body.style.cursor = "grab";

    exploreAnimation();
    gsap.to(annotations[currentIndex], { opacity: 1, duration: 0.5 });
  });

  function exploreAnimation() {
    const tlExplore = gsap.timeline();

    action.timeScale = -1;

    tlExplore
      .to(action, {
        duration: 2,
        time: 0,
        ease: "ease:Circ.easeOut",
        onUpdate,
      })
      .to(
        position,
        {
          x: 7.2099849896,
          y: 0.099284364,
          z: 7.0496170851,
          duration: 2.5,
          onUpdate,
          onComplete: () => {
            //log all parts
            for (let i = 0; i < 20; i++) {
              console.log(watch.children[0].children[0].children[i]);
            }
          },
        },
        0.25
      )
      .to(
        target,
        {
          x: 0.0790911847,
          y: -0.1250940841,
          z: 0.421398108,
          duration: 2.5,
          onUpdate,
        },
        "-=2.5"
      )
      .to(
        ".explore--content",
        {
          opacity: 0,
          x: "130%",
          duration: 1.5,
          ease: "power4.out",
          onComplete: onCompleteExplore,
        },
        "-=2.5"
      );
  }

  function onCompleteExplore() {
    exitContainer.style.display = "flex";
    // if (camera.controls) camera.controls.enabled = true;
  }

  function handleExitButtonClick() {
    annotationVisible = false;
    toggleAnnotations(false);
    exploreView.style.pointerEvents = "all";
    canvasView.style.pointerEvents = "none";
    canvasContainer.style.zIndex = "unset";
    document.body.style.overflowY = "auto";
    exitContainer.style.display = "none";
    document.body.style.cursor = "default";
    currentIndex = 0;
    exitAnimation();
  }

  // document.querySelector(".button--exit")?.addEventListener("click", () => {
  //   annotationVisible = false;
  //   toggleAnnotations(false);
  //   exploreView.style.pointerEvents = "all";
  //   canvasView.style.pointerEvents = "none";
  //   canvasContainer.style.zIndex = "unset";
  //   document.body.style.overflowY = "auto";
  //   exitContainer.style.display = "none";
  //   document.body.style.cursor = "default";
  //   currentIndex = 0;
  //   exitAnimation();
  // });

  // EXIT EVENT

  function exitAnimation() {
    if (camera.controls) camera.controls.enabled = false;
    action.timeScale = 1;

    const tlExit = gsap.timeline();

    tlExit
      .to(action, {
        duration: 2,
        time: action.getClip().duration,
        ease: "ease:Circ.easeOut",
        onUpdate,
      })
      .to(
        position,
        {
          x: -8.6287861477,
          y: 1.838931316,
          z: 8.9131383423,
          duration: 1.2,
          ease: "power4.out",
          onUpdate,
        },
        0.2
      )
      .to(
        target,
        {
          x: 1.0142997171,
          y: -0.1216143553,
          z: 1.302559028,
          duration: 1.2,
          ease: "power4.out",
          onUpdate,
        },
        0.2
      )
      .to(
        ".explore--content",
        {
          opacity: 1,
          x: "0%",
          duration: 0.5,
          ease: "power4.out",
        },
        "-=2.5"
      );
  }

  document
    .querySelector(".button--colors.silver")
    ?.addEventListener("click", () => {
      changeColor(new Color(0xc0c3c3).convertSRGBToLinear());
    });

  document
    .querySelector(".button--colors.blue")
    ?.addEventListener("click", () => {
      changeColor(new Color(0x247bca).convertSRGBToLinear());
    });

  document
    .querySelector(".button--colors.green")
    ?.addEventListener("click", () => {
      changeColor(new Color(0x1d9e1d).convertSRGBToLinear());
    });
  document
    .querySelector(".button--colors.black")
    ?.addEventListener("click", () => {
      changeColor(new Color(0x100f0f).convertSRGBToLinear());
    });
  document
    .querySelector(".button--colors.red")
    ?.addEventListener("click", () => {
      changeColor(new Color(0x882121).convertSRGBToLinear());
    });

  function changeColor(_colorToBeChanged: Color) {
    drillMaterial.color = _colorToBeChanged;
    drillMaterial2.color = _colorToBeChanged;
    viewer.scene.setDirty();
  }
}

setupViewer();

// nextButton.addEventListener("click", () => {
//   if (currentIndex < annotationPositions.length - 1) {
//     currentIndex++;
//     animateCamera(currentIndex); // Move camera to new position
//     // switchAnnotation(currentIndex); // Switch to the new annotation
//   }
// });

// backButton.addEventListener("click", () => {
//   if (currentIndex > 0) {
//     currentIndex--;
//     animateCamera(currentIndex); // Move camera to new position
//     // switchAnnotation(currentIndex); // Switch to the new annotation
//   }
// });
